import React, { useEffect, useState } from 'react';
import { Icon, IconsEnum } from 'components/Icons';
import {
  ImpostazioniDiminuzioneImporto,
  ImpostazioniVariazioniQuote,
} from 'lib/api/sport/sportScommesseImpostazioniApi';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';

import { Button } from 'components/button';
import { CarrelloImpostazioniSection } from 'features/carrello/carrelloImpostazioni/carrelloImpostazioniSection';
import { RadioButton } from 'components/inputs/radioButton';
import { Skeleton } from 'components/skeleton';
import { ToggleSwitch } from 'components/inputs/toggleSwitch';
import { isTruthy } from 'utility/functions';
import { selectIsActiveDiminuzioneImporto } from 'features/configuration/selectors';
import { selectIsOpenSettings } from 'features/sport/components/sportTicket/sportTicketSelectors';
import styles from './CarrelloImpostazioni.module.scss';
import { toggleOpenSettings } from 'features/sport/components/sportTicket/sportTicketSlice';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import { useInternalSession } from 'hooks/useInternalSession';
import { useUpdateImpostazioniScommessaMutation } from 'features/api/sportApiSlice';
import { updateUserAttribute } from 'features/auth/authSlice';

export const CarrelloImpostazioni = () => {
  const { session } = useInternalSession();

  const isActiveDiminuzioneImporto = useTypedSelector(selectIsActiveDiminuzioneImporto);

  const [variazioneQuote, setVariazioniQuote] = useState<string>(
    session?.user?.quote_smart || ImpostazioniVariazioniQuote.AccettaSempre
  );
  const [diminuzioneImporto, setDiminuzioneImporto] = useState<string>(
    session?.user?.flag_acc_rid_import || ImpostazioniDiminuzioneImporto.NonAccettareInAutomatico
  );
  const [hasBonusGold, setHasBonusGold] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const isOpenSettings = useTypedSelector(selectIsOpenSettings);

  const [updateImpostazioniScommessa, { isLoading: isLoadingUpdateImpostazioniScommessa }] =
    useUpdateImpostazioniScommessaMutation();

  const handleSubmit = async (event) => {
    event.preventDefault();
    const response = await updateImpostazioniScommessa({
      variazioniQuote: String(variazioneQuote),
      applyBonusGold: hasBonusGold ? '1' : '0',
      riduzioneImporto: String(diminuzioneImporto),
    }).unwrap();
    if (response.statusCodeResponse === 1024) {
      dispatch(
        updateUserAttribute({
          flg_bw: hasBonusGold ? '1' : '0',
          quote_smart: String(variazioneQuote),
          flag_acc_rid_import: String(diminuzioneImporto),
        })
      );
    }
  };

  useEffect(() => {
    if (!!session?.user) {
      setHasBonusGold(isTruthy(session.user.flg_bw));
    }
  }, [session]);

  const [
    labelImpostazioniScommessa,
    labelVariazioniQuote,
    labelButton,
    labelBonusGoldPrio,
    labelTutteVariazioni,
    labelVariazioniAlte,
    labelNoModificheQuote,
    labelApplicazioneBonus,
    labelTrattamentoImporto,
    labelToggleOn,
    labelToggleOff,
    labelDiminuzioneImporto,
    labelAccettaInAutomatico,
    labelNonAccettareInAutomatico,
  ] = useGetLabelsByKeys([
    'impostazioni-scommessa',
    'variazioni-quote',
    'salva-impostazioni',
    'bonus-gold-prio',
    'accetta-tutte-variazioni',
    'accetta-variazioni-alte',
    'no-modifiche-quote',
    'applicazione-bonus',
    'importo-scalato',
    'toggle-attivo',
    'toggle-non-attivo',
    'diminuzione-importo',
    'accetta-in-automatico',
    'non-accettare-in-automatico',
  ]);
  return (
    <section className={`${styles.container} ${isOpenSettings && styles.visibile}`}>
      <div className={styles.header}>
        <button type="button" className={styles.headerBtn} onClick={() => dispatch(toggleOpenSettings(false))}>
          <Icon className={styles.headerIcon} iconId={IconsEnum.ARROW_BACK} color="var(--color-primary)" />
        </button>
        <span className={styles.headerLabel}>{labelImpostazioniScommessa}</span>
      </div>
      {session === undefined ? (
        <Skeleton rows={4} />
      ) : (
        <form onSubmit={handleSubmit}>
          <CarrelloImpostazioniSection title={labelVariazioniQuote}>
            <RadioButton
              isVertical
              name={labelVariazioniQuote}
              options={[
                { label: labelTutteVariazioni, value: ImpostazioniVariazioniQuote.AccettaSempre },
                { label: labelVariazioniAlte, value: ImpostazioniVariazioniQuote.AccettaSoloSeMaggiore },
                { label: labelNoModificheQuote, value: ImpostazioniVariazioniQuote.NonAccettareCambioQuota },
              ]}
              defaultValue={String(variazioneQuote)}
              onChange={(e) => setVariazioniQuote(e.target.value)}
              className={styles.radiobutton}
            />
          </CarrelloImpostazioniSection>
          {isActiveDiminuzioneImporto && (
            <CarrelloImpostazioniSection title={labelDiminuzioneImporto}>
              <RadioButton
                isVertical
                name={labelDiminuzioneImporto}
                options={[
                  { label: labelAccettaInAutomatico, value: ImpostazioniDiminuzioneImporto.AccettaInAutomatico },
                  {
                    label: labelNonAccettareInAutomatico,
                    value: ImpostazioniDiminuzioneImporto.NonAccettareInAutomatico,
                  },
                ]}
                defaultValue={String(diminuzioneImporto)}
                onChange={(e) => setDiminuzioneImporto(e.target.value)}
                className={styles.radiobutton}
              />
            </CarrelloImpostazioniSection>
          )}
          {+`${session?.user?.flag_wagering ?? '0'}` > 0 && (
            <CarrelloImpostazioniSection title={labelApplicazioneBonus}>
              <div className={styles.toggleContainer}>
                <p className={styles.paragraphBig}>{labelBonusGoldPrio}</p>
                <ToggleSwitch
                  className="Toggle"
                  aria-label="Bonus Gold"
                  pressed={hasBonusGold}
                  onPressedChange={setHasBonusGold}
                >
                  {hasBonusGold ? labelToggleOn : labelToggleOff}
                </ToggleSwitch>
              </div>
              <p className={styles.paragraph}>{labelTrattamentoImporto}</p>
            </CarrelloImpostazioniSection>
          )}

          <Button type="submit" className={styles.submitBtn} isLoading={isLoadingUpdateImpostazioniScommessa}>
            {labelButton}
          </Button>
        </form>
      )}
    </section>
  );
};